'use client';

import { cn } from '@/lib/cn';
import { Tooltip } from '@/components/Tooltip';

type Translate = (key: string) => string;

type CommandPaletteTriggerProps = {
  collapsed: boolean;
  onOpen: () => void;
  t: Translate;
};

const metaKeyClassName =
  'inline-flex items-center rounded border border-base-content/10 bg-base-200/70 px-1.5 py-0.5 text-[10px] font-medium leading-none text-[var(--color-fg-subtle)]';

export const CommandPaletteTrigger = ({ collapsed, onOpen, t }: CommandPaletteTriggerProps) => {
  const label = t('commandPaletteTriggerLabel');
  const metaKey = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent) ? '⌘' : 'Ctrl';

  if (collapsed) {
    return (
      <Tooltip content={label} placement="right" mode="overflowSafe">
        <button
          type="button"
          aria-label={label}
          onClick={onOpen}
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-md text-[var(--color-fg-muted)] transition-colors',
            'hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-fg)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]',
          )}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </button>
      </Tooltip>
    );
  }

  return (
    <button
      type="button"
      aria-label={label}
      onClick={onOpen}
      className={cn(
        'group flex h-9 w-full items-center gap-2 rounded-md border border-base-300 px-2 text-xs',
        'bg-[var(--color-bg-elevated)] text-[var(--color-fg-muted)]',
        'hover:border-base-content/20 hover:text-[var(--color-fg)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]',
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 shrink-0 opacity-60 group-hover:opacity-80"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
      <span className="flex-1 truncate text-left">{label}</span>
      <span className="flex items-center gap-0.5">
        <span className={metaKeyClassName}>{metaKey}</span>
        <span className={metaKeyClassName}>K</span>
      </span>
    </button>
  );
};
